/**
 * @file DayPlanSettings.js
 * @version 2.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-07
 * @modified 2026-10-15
 * @description Transport Settings → Lorry Day Planning: the switch for the
 *              bulk planning board's lorry day planning (transport_day_plan,
 *              off by default; 2.0.0 — it replaced Loads and Drops). Everyone with transport settings sees it;
 *              only an admin can turn it on or off (the server refuses
 *              anyone else, and audits the change). When the server's
 *              TRANSPORT_DAY_PLAN env var is set, it decides and the toggle
 *              is greyed with the reason. Server: GET/PUT
 *              /api/transport/day-plan/switch, services/transportDayPlanSwitch.
 *
 *              2.1.0 (2026-10-15, Cam): switching ON stores the planning as it
 *              is first (services/transportBaselineService). Switching OFF
 *              opens a window: "Revert planning to how it was before lorry
 *              day planning was turned on (date, time, by whom)?", with what
 *              would change day by day (GET /day-plan/revert-preview) — Turn
 *              off and revert / Turn off and keep today's planning / Cancel
 *              (POST /day-plan/disable). Past days are never touched; days
 *              locked by hand are left alone.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './DayPlanSettings.css';
import LoadingSpinner from './LoadingSpinner';
import Modal from './Modal';
import { useSlideOutButton } from './SlideOutButton';
import { formatDateTimeLondon } from '../utils/dateFormatters';

/** 'YYYY-MM-DD' → "Fri 16/10/2026", read as that calendar day. */
const dayLabel = (d) => {
  const [y, m, dd] = String(d).split('-').map(Number);
  const wd = new Date(Date.UTC(y, m - 1, dd)).toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
  return `${wd} ${String(dd).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
};
const STATUS_WORDS = {
  revert: 'Will be put back',
  past: 'Past — never touched',
  locked: 'Locked — left alone (unlock it to revert it)',
  unchanged: 'Nothing to put back'
};
const ITEMS_SHOWN = 8;

/** The switch-off window's body: the question, and what would change day by day. */
function SwitchOffWindow({ preview, loading, error, result }) {
  const baseline = preview?.baseline || null;
  const plan = preview?.plan || null;
  const days = plan?.days || [];
  const totals = plan?.totals || null;
  return (
    <>
      <div className="dps-window-body">
        {loading && <LoadingSpinner />}
        {error && <p className="dps-error">{error}</p>}
        {result && (
          <div className="dps-result">
            <p><strong>Lorry day planning is off.</strong>{' '}
              {result.reverted
                ? `${result.result?.reverted?.length || 0} day${result.result?.reverted?.length === 1 ? '' : 's'} put back as planned before it was turned on.`
                : 'Today\'s planning was kept.'}</p>
            {(result.result?.missing || []).length > 0 && (
              <>
                <p className="dps-meta">Left alone (no longer on the day):</p>
                <ul className="dps-items">
                  {result.result.missing.map((m, i) => <li key={i}>{dayLabel(m.date)} — {m.label}: {m.why}</li>)}
                </ul>
              </>
            )}
          </div>
        )}
        {!loading && !result && preview && (
          <>
            {baseline ? (
              <p className="dps-question">
                Revert planning to how it was before lorry day planning was turned on
                ({preview.switchedOn || formatDateTimeLondon(baseline.as_at)})?
              </p>
            ) : (
              <p className="dps-notice">{preview.note || 'No saved pre-switch planning.'} Switching off keeps today&apos;s planning.</p>
            )}
            {baseline && totals && (
              <p className="dps-meta">
                {totals.revert
                  ? `${totals.revert} day${totals.revert === 1 ? '' : 's'} would change (${totals.changes} change${totals.changes === 1 ? '' : 's'}).`
                  : 'Nothing has changed since it was turned on — reverting changes nothing.'}
                {' '}Past days are never touched.
                {totals.locked ? ` ${totals.locked} locked day${totals.locked === 1 ? ' is' : 's are'} left alone.` : ''}
                {baseline.source === 'reconstructed' ? ' (Saved afterwards from the audit trail, as it stood at the moment it was turned on.)' : ''}
              </p>
            )}
            {baseline && days.length > 0 && (
              <ul className="dps-days">
                {days.map(d => (
                  <li key={d.date} className={`dps-day dps-day-${d.status}`}>
                    <div className="dps-day-head">
                      <span className="dps-day-date">{dayLabel(d.date)}</span>
                      <span className="dps-day-status">{STATUS_WORDS[d.status] || d.status}</span>
                    </div>
                    <div className="dps-day-summary">{d.summary}</div>
                    {d.items.length > 0 && (
                      <ul className="dps-items">
                        {d.items.slice(0, ITEMS_SHOWN).map((it, i) => <li key={i}>{it}</li>)}
                        {d.items.length > ITEMS_SHOWN && <li>… and {d.items.length - ITEMS_SHOWN} more</li>}
                      </ul>
                    )}
                    {d.missing.length > 0 && (
                      <p className="dps-meta">Left alone: {d.missing.map(m => `${m.label} (${m.why})`).join('; ')}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </>
  );
}

/** The window's footer: its own component so the slide-out buttons are measured as they mount. */
function SwitchOffFooter({ preview, loading, busy, result, onRevert, onKeep, onCancel }) {
  useSlideOutButton([preview, loading, busy, result]);
  const baseline = preview?.baseline || null;
  return (
    <div className="buttons slideout">
        {result ? (
          <div><button className="btn-slideout" onClick={onCancel}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">done</span></div>
            <span className="btn-label">Close</span>
          </button></div>
        ) : (
          <>
            <div><button className="btn-slideout" onClick={onCancel} disabled={busy}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button></div>
            <div><button className="btn-slideout" onClick={onKeep} disabled={busy || loading || !preview}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">toggle_off</span></div>
              <span className="btn-label">{busy === 'keep' ? 'Turning off…' : 'Turn off and keep today\'s planning'}</span>
            </button></div>
            <div><button className="btn-slideout" onClick={onRevert} disabled={busy || loading || !baseline}
              title={!baseline ? 'There is no saved pre-switch planning to put back' : undefined}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">history</span></div>
              <span className="btn-label">{busy === 'revert' ? 'Reverting…' : 'Turn off and revert'}</span>
            </button></div>
          </>
        )}
    </div>
  );
}

function DayPlanSettings() {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/transport/day-plan/switch`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not read the switch');
      setState(d);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Switching off: the window first (revert or keep).
  const [offOpen, setOffOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [offError, setOffError] = useState(null);
  const [offBusy, setOffBusy] = useState(null);
  const [offResult, setOffResult] = useState(null);

  const openSwitchOff = async () => {
    setOffOpen(true);
    setPreview(null);
    setOffError(null);
    setOffResult(null);
    setPreviewLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/transport/day-plan/revert-preview`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not work out what would change');
      setPreview(d);
    } catch (e) {
      setOffError(e.message);
      setPreview({ baseline: null, plan: null, note: 'What would change could not be worked out.' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const switchOff = async (revert) => {
    setOffBusy(revert ? 'revert' : 'keep');
    setOffError(null);
    try {
      const r = await fetch(`${API_URL}/api/transport/day-plan/disable`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ revert, baselineId: preview?.baseline?.id || null })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not switch lorry day planning off');
      setOffResult(d);
      await load();
    } catch (e) {
      setOffError(e.message);
    } finally {
      setOffBusy(null);
    }
  };

  const closeSwitchOff = () => {
    if (offBusy) return;
    setOffOpen(false);
  };

  const toggle = async (enabled) => {
    if (!enabled) { openSwitchOff(); return; }
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/transport/day-plan/switch`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not change the switch');
      setNote(d.note || null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const blocked = !state ? 'Loading'
    : state.env !== null ? `Set on the server (${state.reason}) — it decides, not this switch`
    : !state.canChange ? 'Only an admin can switch lorry day planning on or off'
    : null;
  const changedAt = state?.changedAt
    ? new Date(state.changedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="search-layout day-plan-settings">
      <div className="search-header">
        <div className="workflow-info">
          <span className="info">Lorry Day Planning</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span>
              Plans each lorry&apos;s day on the bulk board as the driver will run it: every order collected at its
              collection time and dropped at its drop time, in the compartments chosen (C1 nearest the cab).
              Off, the board works exactly as before.
            </span>
          </span>
        </div>
      </div>

      <div className="dps-panel">
        {error && <p className="dps-error">{error}</p>}
        {note && <p className="dps-notice">{note}</p>}
        {!state && !error && <LoadingSpinner />}
        {state && (
          <>
            <div className="dps-row" title={blocked || undefined}>
              <div className="checkbox-container">
                <div className="check light">
                  <input
                    id="day-plan-switch"
                    type="checkbox"
                    checked={!!state.enabled}
                    disabled={!!blocked || saving}
                    onChange={(e) => toggle(e.target.checked)}
                  />
                  <label htmlFor="day-plan-switch"></label>
                </div>
              </div>
              <span className="dps-state">{state.enabled ? 'On' : 'Off'}</span>
              {saving && <span className="dps-meta">Saving…</span>}
            </div>
            {blocked && <p className="dps-meta">{blocked}.</p>}
            {changedAt && <p className="dps-meta">Last changed {changedAt}{state.changedBy ? ` by ${state.changedBy}` : ''}.</p>}
            {!state.ready && (
              <p className="dps-notice">
                The database is not set up for lorry day planning yet (migration transport_line_plans.sql). The
                board can show the days, but drop times, compartments and door layouts cannot be saved until it has run.
              </p>
            )}
            <ul className="dps-list">
              <li><strong>The board</strong> is grouped by haulier, driver and vehicle. Each row has a collection time and a drop time, its compartments, and a tick: planned, part-planned or not planned. Drag rows onto another lorry to assign them.</li>
              <li><strong>A lorry&apos;s day</strong> (&ldquo;Open day&rdquo;) shows every collection and drop in time order and the lorry at each stop: drag its doors, open any door, put each product in its compartments. &ldquo;Suggest&rdquo; fills only what is blank.</li>
              <li><strong>Checks are guidance</strong> — over payload, too big for its compartments, a compartment already in use, dropped before it is collected, a medicated compartment to sweep out, weight balance on a part load. They never stop a save and never print.</li>
              <li><strong>Planners&apos; notes</strong> on an order stay on the board: never printed, emailed or sent.</li>
              <li>&ldquo;Combine into one run&rdquo; is not used while this is on: each collection has its own time.</li>
              <li><strong>Each day keeps the rules it was planned under.</strong> The day it is switched on and every day before it are shown and printed as they were planned (their paperwork went out that way); later days are planned with lorry day planning.</li>
              <li><strong>Switching on saves the planning as it is</strong> (today onward). Switching off offers to put it back — with what would change, day by day — or to keep today&apos;s planning. Past days are never touched.</li>
            </ul>
          </>
        )}
      </div>
      <Modal
        isOpen={offOpen}
        onClose={closeSwitchOff}
        title="Turn lorry day planning off"
        size="lg"
        className="day-plan-switch-off"
        footer={(
          <SwitchOffFooter
            preview={preview}
            loading={previewLoading}
            busy={offBusy}
            result={offResult}
            onRevert={() => switchOff(true)}
            onKeep={() => switchOff(false)}
            onCancel={closeSwitchOff}
          />
        )}
      >
        <SwitchOffWindow preview={preview} loading={previewLoading} error={offError} result={offResult} />
      </Modal>
    </div>
  );
}

export default DayPlanSettings;
